import Joi, { ValidationError } from 'joi';
import { bannedPasswordParts } from 'src/app/components/bannedPasswordParts';

/**
 * Used to validate input files against list of validator rules (eg. required, min, email)
 * @param  {...any} validators Custom functions to validate input
 * @example
 *  <Field
 *    name="queuename"
 *    validate={composeValidators(required(), creditCard())}
 *    component="input"
 *  />
 */
export const composeValidators = (...validators: [any]) => (value: any) =>
  validators.reduce((error, validator) => error || validator(value), undefined);

/**
 * Validate input is not empty
 * @param {string} message Custom error message to show customer
 */
export const required = (message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.string().required().error(new Error('Required')),
  });
  const error = schema.validate({ value }).error;
  return error ? message || error.message : undefined;
};

/**
 * Validate length of string is minimum specified
 * NOTE: max is handled via HTML 5
 * @param {number} min Minimum length of input
 * @param {string} message Custom error message to show customer
 */
export const min = (min: number, message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.string()
      .min(min)
      .error(new Error(`Must be at least ${min}`)),
  });
  const error = schema.validate({ value }).error;
  return error ? message || error.message : undefined;
};

/**
 * Validate input is number
 * @param {string} message Custom error message to show customer
 */
export const number = (message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.number().error(new Error('Must be a number')),
  });
  const error = schema.validate({ value }).error;
  return error ? message || error.message : undefined;
};

/**
 * Validates email using regex
 * NOTE: tlds - options for TLD (top level domain) validation.
 *  By default, the TLD must be a valid name listed on the IANA registry.
 *  To disable validation, set tlds to false
 * @param {string} message Custom error message to show customer
 * @see https://github.com/hapijs/hapi/issues/4059
 */
export const email = (message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.string()
      .email({ tlds: { allow: false } })
      .error(new Error('Must be an email')),
  });
  const error = schema.validate({ value }).error;
  return error ? message || error.message : undefined;
};

/**
 * Validates value is comma separated email address
 * NOTE: tlds - options for TLD (top level domain) validation.
 *  By default, the TLD must be a valid name listed on the IANA registry.
 *  To disable validation, set tlds to false
 * @param {string} message Custom error message to show customer
 * @see https://github.com/hapijs/hapi/issues/4059
 */
export const commaSeparatedEmails = (message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.string()
      .email({ tlds: { allow: false } })
      .error(new Error('Must be an email')),
  });
  // split list of emails by comma and validate each email separately
  const error = value.split(',').reduce(
    // short-circuite if first error found
    (firstError: ValidationError | undefined, email: string) =>
      firstError || schema.validate({ value: email.trim() }).error,
    undefined
  );
  return error ? message || error.message : undefined;
};

/**
 * Validates credit card number using Using Luhn Algorithm
 * @param {string} message Custom error message to show customer
 */
export const creditCard = (message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.string().creditCard().error(new Error('Invalid credit card')),
  });
  const error = schema.validate({ value }).error;
  return error ? message || error.message : undefined;
};

/**
 * Validates value is valid uri
 * @param {string} message Custom error message to show customer
 */
export const uri = (message?: string) => (value: any) => {
  const schema = Joi.object({
    value: Joi.string().uri().error(new Error('Invalid uri')),
  });
  const error = schema.validate({ value }).error;
  return error ? message || error.message : undefined;
};

/**
 * Regular expression validation
 * @param {string} pattern Javascript compliant regex pattern
 * @param {string} message Custom error message to show customer
 */
export const pattern = (pattern: string, message?: string) => (value: any) =>
  typeof value === 'undefined' || value === null || value.match(pattern)
    ? undefined
    : message || 'Does not match expected pattern';

/**
 * Ensures month/year must be in future
 * @param {number} month Input month
 * @param {number} year Input year
 * @param {string} message Custom error message to show customer
 * @param {number?} currentMonth Optional current month override
 * @param {number?} currentYear Optional current year override
 */
export const currentDate = (
  month: number,
  year: number,
  message?: string,
  currentMonth: number = new Date().getMonth(),
  currentYear: number = new Date().getFullYear()
) =>
  (month >= currentMonth && year >= currentMonth) || year > currentYear
    ? undefined
    : message || 'Expiration date cannot be in the past';

/**
 * Return true if value is URL
 *
 * @param value Possible URL
 *
 * @returns True if value is URL
 */
export const isUrl = (value: string | undefined | null): boolean =>
  !!value && !uri()(value);

/**
 * Return value if value is URL otherwise empty string
 *
 * @typedef {function(string)} pipeIsUrlCallback
 * @callback pipeIsUrlCallback
 * @param {string} value Possible URL
 *
 * @returns {string} Value if URL else empty string
 */

/**
 * Return value if value is URL otherwise empty string
 *
 * NOTE: This function is pipeable meaning this function takes in a string and returns a string
 * so this function can be chained with other string pipeable functions
 *
 * @param {boolean} [not=false] If true then reverse logic
 * @param {string} value Possible URL
 *
 * @returns {pipeIsUrlCallback} Callback that returns [value] if string is url else returns empty string (reversed if not is true)
 */
export const pipeIsUrl = (not: boolean = false) => (value: string): string =>
  isUrl(value) !== not ? value : '';

/**
 * Pipeline processing using a value and set of pipeable operators
 *
 * @param value Parameter to pass
 * @param pipeOperators Pipeable pure functions (NOTE: functions should not mutate input)
 *
 * @returns Value (cloned) after processing through pipeline
 *
 * @example
 *  const upperCaseUrls = pipe(myUrl, pipeIsUrl(true), pipeToUpperCase)
 */
export const pipe = <T>(
  value: T | (T & any[])[number][],
  ...pipeOperators: any[]
): T | (T & any[])[number][] => {
  let pipeline = value; // can be assigned since each pipeable operator should not modify the input

  // pass to pipeable operators
  for (const operator of pipeOperators) {
    pipeline = operator(pipeline);
  }

  return pipeline;
};

/**
 * Validate a password to ensure it does not contain banned words.
 * @param {string} password
 * @returns {valid: boolean, word: string} password is valid or invalid
 */
export function isPasswordValid(password: string) {
  const lowerPassword = password.toLowerCase();
  for (const bannedWord of bannedPasswordParts) {
    if (lowerPassword.includes(bannedWord)) {
      return { valid: false, word: bannedWord };
    }
  }
  return { valid: true };
}
